import { DocumentCheckIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { ToggleSwitch } from '@/components/legacy-ui/ToggleSwitch';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { useExportLOA } from '@/hooks/useReportExport';
import { useAuth } from '@/state/auth';
import { RiskStatus } from '@/types';

const DEFAULT_LICENSING_LINE =
  'Praetorian is independently certified by **CREST** to conduct Penetration Testing services.';

const getTodayISO = (): string => new Date().toISOString().split('T')[0];

// formatDate turns an ISO date (YYYY-MM-DD) into "February 13, 2026". Empty
// input yields '' so callers can fall back to a placeholder.
function formatDate(iso: string): string {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// defaultContent seeds the editable LOA body. %%FINDINGS%% is expanded
// server-side with the severity breakdown computed from the selected risks.
function defaultContent(
  clientName: string,
  startDate: string,
  endDate: string
): string {
  const c = clientName.trim() || 'the client';
  const start = formatDate(startDate) || '[START DATE]';
  const end = formatDate(endDate) || '[END DATE]';
  return `## Security Evaluation Performed by Independent Experts

The undersigned attests that between ${start} and ${end}, Praetorian completed a security assessment of the ${c} environment. This assessment involved a time-boxed, best-effort approach covering the in-scope systems.

During the assessment, Praetorian identified the following issues:

%%FINDINGS%%

The testing methodology was informed by industry-standard frameworks and best practices, including the OWASP Web Security Testing Guide, the OWASP Top Ten (2021), and the Penetration Testing Execution Standard (PTES). All findings were assessed and rated using the Common Vulnerability Scoring System (CVSS) framework to ensure consistent and standardized risk evaluation.

As ${c}'s business continues to change, so will its overall security posture. Such changes will affect the validity of Praetorian's findings and this letter. Therefore, any statements made by Praetorian only describe a "snapshot" in time.

Praetorian would like to thank ${c} for this opportunity to help the organization evaluate its current security posture.`;
}

interface LOAFormValues {
  clientName: string;
  assessmentName: string;
  startDate: string;
  endDate: string;
  issueDate: string;
  undersignedName: string;
  undersignedDesignation: string;
  undersignedEmail: string;
  licensingLine: string;
  content: string;
  draft: boolean;
}

export interface GenerateLOAProps {
  /** Pre-selected risk keys; severity counts are computed from these (else open risks). */
  selectedRows?: string[];
  /** Externally controlled open state (parent renders the trigger). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * GenerateLOA — Praetorian-only "Generate LOA" button + modal. Posts to
 * /export/loa, which renders a standalone Letter of Attestation via orator.
 */
export function GenerateLOA({
  selectedRows = [],
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}: GenerateLOAProps) {
  const isExternallyControlled =
    externalOpen !== undefined && externalOnOpenChange !== undefined;

  const { me, isPraetorianUser } = useAuth();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isExternallyControlled ? externalOpen : internalOpen;
  const setOpen = isExternallyControlled
    ? externalOnOpenChange
    : setInternalOpen;

  const [form, setForm] = useState<LOAFormValues>(() => ({
    clientName: '',
    assessmentName: '',
    startDate: '',
    endDate: '',
    issueDate: getTodayISO(),
    undersignedName: '',
    undersignedDesignation: '',
    undersignedEmail: me,
    licensingLine: DEFAULT_LICENSING_LINE,
    content: defaultContent('', '', ''),
    draft: false,
  }));
  const [contentEdited, setContentEdited] = useState(false);

  const { mutateAsync: exportLOA, status } = useExportLOA();
  const isGenerating = status === 'pending';

  if (!isPraetorianUser) {
    return null;
  }

  function setField<K extends keyof LOAFormValues>(
    field: K,
    value: LOAFormValues[K]
  ) {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Keep the content template in sync with the client name and assessment
      // dates until the user edits the body themselves.
      if (
        (field === 'clientName' ||
          field === 'startDate' ||
          field === 'endDate') &&
        !contentEdited
      ) {
        next.content = defaultContent(
          next.clientName,
          next.startDate,
          next.endDate
        );
      }
      return next;
    });
  }

  async function handleGenerate() {
    setOpen(false);
    try {
      await exportLOA({
        risk_keys: selectedRows.length > 0 ? selectedRows : undefined,
        status_filter: [RiskStatus.Opened],
        config: {
          client_name: form.clientName,
          assessment_name: form.assessmentName,
          issue_date: form.issueDate,
          undersigned_name: form.undersignedName,
          undersigned_designation: form.undersignedDesignation,
          undersigned_email: form.undersignedEmail,
          licensing_line: form.licensingLine,
          content: form.content,
          draft: form.draft,
        },
      });
    } catch {
      // toast handled by useExportLOA
    }
  }

  const inputClass =
    'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-chariot-brand-primary rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-0 focus:outline-hidden';

  return (
    <>
      {!isExternallyControlled && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="generate-loa-button"
          aria-label="Generate Letter of Attestation"
          className="rounded-full"
        >
          <DocumentCheckIcon className="size-4" />
          Generate LOA
        </Button>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        hideClose
        size="4xl"
        title={
          <h2 className="py-2 text-lg font-bold">Letter of Attestation</h2>
        }
        className="scrollbar-dark border-chariot-stroke border-2 border-x-0 p-0"
        footer={{
          text: isGenerating ? 'Generating...' : 'Generate LOA',
          isLoading: isGenerating,
          disabled:
            isGenerating ||
            form.clientName.trim() === '' ||
            form.content.trim() === '',
          onClick: handleGenerate,
        }}
      >
        <div className="scrollbar-dark max-h-[calc(100vh-14rem)] overflow-auto px-6 py-4">
          <div className="flex flex-col gap-5">
            {selectedRows.length > 0 && (
              <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border px-4 py-3 text-sm">
                Severity counts computed from {selectedRows.length} selected{' '}
                {selectedRows.length === 1 ? 'finding' : 'findings'}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Client Name</label>
                <input
                  type="text"
                  className={inputClass}
                  value={form.clientName}
                  onChange={e => setField('clientName', e.target.value)}
                  placeholder="Acme Corp"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Issue Date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.issueDate}
                  onChange={e => setField('issueDate', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Start Date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.startDate}
                  onChange={e => setField('startDate', e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">End Date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.endDate}
                  onChange={e => setField('endDate', e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Assessment Name</label>
              <input
                type="text"
                className={inputClass}
                value={form.assessmentName}
                onChange={e => setField('assessmentName', e.target.value)}
                placeholder="2026 Application and External Network Penetration Test"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Undersigned Name</label>
                <input
                  type="text"
                  className={inputClass}
                  value={form.undersignedName}
                  onChange={e => setField('undersignedName', e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Title</label>
                <input
                  type="text"
                  className={inputClass}
                  value={form.undersignedDesignation}
                  onChange={e =>
                    setField('undersignedDesignation', e.target.value)
                  }
                  placeholder="Offensive Security Manager, Praetorian"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Email</label>
                <input
                  type="email"
                  className={inputClass}
                  value={form.undersignedEmail}
                  onChange={e => setField('undersignedEmail', e.target.value)}
                  placeholder="jane.doe@praetorian.com"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Licensing Line</label>
              <input
                type="text"
                className={inputClass}
                value={form.licensingLine}
                onChange={e => setField('licensingLine', e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Content</label>
              <textarea
                className={`${inputClass} min-h-[260px] font-mono`}
                value={form.content}
                onChange={e => {
                  setContentEdited(true);
                  setField('content', e.target.value);
                }}
              />
              <p className="text-chariot-text-secondary text-xs">
                Markdown. <code className="text-xs">%%FINDINGS%%</code> is
                replaced with the severity breakdown computed from the selected
                findings.
              </p>
            </div>

            <div className="flex items-center justify-between py-2.5">
              <span className="text-sm font-medium">Draft watermark</span>
              <ToggleSwitch
                checked={form.draft}
                onChange={checked => setField('draft', checked)}
                ariaLabel="Draft watermark"
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
